import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Trash, Heart, PencilSimple } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";
import { formatHeight } from "@/lib/heights";
import PaginationLoader from "@/components/PaginationLoader";
import StoryComposer from "@/components/StoryComposer";
import { track } from "@/lib/track";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 12;
// Minimum time the giraffe pagination loader shows before the next batch of
// stories is revealed below it (matches the product grid).
const PAGINATION_LOADER_MS = 3000;
const ADMIN_EMAIL = "swarajrp030@gmail.com";

const Avatar = ({ name, picture }) =>
  picture ? (
    <img src={picture} alt={name} className="w-9 h-9 rounded-full object-cover" />
  ) : (
    <div
      className="w-9 h-9 rounded-full bg-[#2D302A] text-white flex items-center justify-center text-xs uppercase"
      style={{ fontFamily: "IBM Plex Mono, monospace" }}
    >
      {(name || "?").charAt(0)}
    </div>
  );

const GENDER_TAG = { male: "Men", female: "Women" };

// Height + Men/Women chips, styled like the product card badges.
export const StoryTags = ({ story, className = "" }) => {
  const tags = [];
  if (story.user_height) tags.push(formatHeight(story.user_height));
  if (GENDER_TAG[story.user_gender]) tags.push(GENDER_TAG[story.user_gender]);
  if (tags.length === 0) return null;
  return (
    <span className={`flex flex-wrap gap-1.5 ${className}`}>
      {tags.map((t) => (
        <span
          key={t}
          className="text-[10px] uppercase tracking-[0.15em] text-[#6B6A68] bg-[#F0EFEB] px-2 py-0.5 rounded-sm"
          style={{ fontFamily: "IBM Plex Mono, monospace" }}
        >
          {t}
        </span>
      ))}
    </span>
  );
};

const Stories = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [me, setMe] = useState(null);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [editingStory, setEditingStory] = useState(null);

  // Keep latest state readable from the observer callback without re-creating it.
  const stateRef = useRef({ loading: true, loadingMore: false, hasMore: false, count: 0 });

  useEffect(() => {
    stateRef.current = { loading, loadingMore, hasMore, count: stories.length };
  });

  useEffect(() => {
    // Page is public; me is only for showing delete buttons.
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((res) => setMe(res.data))
      .catch(() => setMe(null));
  }, []);

  const fetchStories = useCallback(async ({ reset = false } = {}) => {
    const offset = reset ? 0 : stateRef.current.count;
    if (reset) setLoading(true);
    else setLoadingMore(true);
    const startedAt = Date.now();
    try {
      const res = await axios.get(`${API}/stories`, {
        params: { limit: PAGE_SIZE, offset, sort: "shuffle" },
      });
      const items = res.data?.items || [];

      // On pagination, hold the giraffe loader for its minimum beat before
      // revealing the next batch below the existing stories.
      if (!reset) {
        const wait = PAGINATION_LOADER_MS - (Date.now() - startedAt);
        if (wait > 0) await new Promise((r) => setTimeout(r, wait));
      }

      setStories((prev) => (reset ? items : [...prev, ...items]));
      setHasMore(Boolean(res.data?.has_more));
    } catch (err) {
      console.error("Error fetching stories:", err);
      if (reset) setStories([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // Initial load. With a #story-<id> hash (homepage preview click), the
  // target may sit beyond page 1 (both surfaces share the same weekly-shuffled
  // order), so page forward in ONE async loop until it's in the result set,
  // commit everything at once, then scroll. A cancelled flag makes the
  // StrictMode double-invocation harmless — the first run aborts cleanly
  // instead of racing the second with a competing reset fetch.
  useEffect(() => {
    const hash = location.hash;
    const targetId = hash && hash.startsWith("#story-") ? hash.slice("#story-".length) : null;
    let cancelled = false;
    let cleanupScroll = () => {};

    (async () => {
      setLoading(true);
      let all = [];
      let more = true;
      let pages = 0;
      try {
        while (!cancelled && more && pages < 20) {
          const res = await axios.get(`${API}/stories`, {
            params: { limit: PAGE_SIZE, offset: all.length, sort: "shuffle" },
          });
          all = [...all, ...(res.data?.items || [])];
          more = Boolean(res.data?.has_more);
          pages += 1;
          if (!targetId || all.some((s) => s.id === targetId)) break;
        }
      } catch (err) {
        console.error("Error fetching stories:", err);
      }
      if (cancelled) return;
      setStories(all);
      setHasMore(more);
      setLoading(false);

      if (!targetId) return;
      // Enforce the scroll briefly — late image loads shift layout after the
      // first attempt lands. But the moment the user scrolls themselves, stop:
      // otherwise the loop yanks them back to the target and the two fight,
      // which reads as jitter/lag on the very first scroll.
      let tries = 0;
      let timer = null;
      let userScrolled = false;
      const onUserScroll = () => {
        userScrolled = true;
      };
      // `scroll` fires on our own scrollIntoView too, so key off intent-signals
      // (wheel/touch/keydown) rather than the scroll event itself.
      window.addEventListener("wheel", onUserScroll, { passive: true });
      window.addEventListener("touchmove", onUserScroll, { passive: true });
      window.addEventListener("keydown", onUserScroll);
      cleanupScroll = () => {
        if (timer) clearTimeout(timer);
        window.removeEventListener("wheel", onUserScroll);
        window.removeEventListener("touchmove", onUserScroll);
        window.removeEventListener("keydown", onUserScroll);
      };
      const scroll = () => {
        if (cancelled || userScrolled) return cleanupScroll();
        const node = document.getElementById(`story-${targetId}`);
        if (node && Math.abs(node.getBoundingClientRect().top) > 100) {
          node.scrollIntoView({ block: "start" });
        }
        if (++tries < 10) timer = setTimeout(scroll, 350);
        else cleanupScroll();
      };
      requestAnimationFrame(scroll);
    })();

    return () => {
      cancelled = true;
      cleanupScroll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Infinite scroll sentinel (ref callback so it re-attaches whenever the
  // sentinel remounts after a list re-render).
  const sentinelNodeRef = useRef(null);
  const sentinelRef = useCallback(
    (node) => {
      sentinelNodeRef.current = node;
      if (sentinelRef._observer) {
        sentinelRef._observer.disconnect();
        sentinelRef._observer = null;
      }
      if (!node) return;
      const observer = new IntersectionObserver(
        (entries) => {
          const { loading: l, loadingMore: lm, hasMore: hm } = stateRef.current;
          if (entries[0].isIntersecting && hm && !l && !lm) {
            fetchStories();
          }
        },
        { rootMargin: "400px 0px" }
      );
      observer.observe(node);
      sentinelRef._observer = observer;
    },
    [fetchStories]
  );

  // The observer only fires on intersection CHANGES, so if a fetch was in
  // flight when the sentinel came into view, the guard blocks it and no new
  // event ever comes. Re-check visibility whenever a fetch settles.
  useEffect(() => {
    if (loading || loadingMore || !hasMore) return;
    const node = sentinelNodeRef.current;
    if (!node) return;
    if (node.getBoundingClientRect().top < window.innerHeight + 400) {
      fetchStories();
    }
  }, [loading, loadingMore, hasMore, fetchStories]);

  const canDelete = (story) =>
    me && (me.user_id === story.user_id || me.email === ADMIN_EMAIL);

  const isAdmin = me?.email === ADMIN_EMAIL;

  const handleLike = async (story) => {
    if (!me) {
      // Signed-out engagement intent — a conversion prompt we can now size.
      track("story_like_blocked_signed_out", { story_id: story.id });
      toast.error("Please sign in to heart a story");
      navigate("/login");
      return;
    }
    // GA4 has no recommended "like", so these stay custom. Unliking is tracked
    // separately rather than as a like with a flag, so the reports stay honest.
    track(story.liked ? "story_unlike" : "story_like", {
      story_id: story.id,
      author_id: story.user_id,
      like_count: story.like_count,
    });
    // Optimistic flip; reconcile with the server's answer (or roll back on error).
    const flip = (s, liked, count) =>
      s.id === story.id ? { ...s, liked, like_count: count } : s;
    setStories((prev) =>
      prev.map((s) => flip(s, !story.liked, story.like_count + (story.liked ? -1 : 1)))
    );
    try {
      const res = await axios.post(
        `${API}/stories/${story.id}/like`,
        {},
        { withCredentials: true }
      );
      setStories((prev) => prev.map((s) => flip(s, res.data.liked, res.data.like_count)));
    } catch (err) {
      console.error("Like failed:", err);
      setStories((prev) => prev.map((s) => flip(s, story.liked, story.like_count)));
      toast.error("Could not update your heart");
    }
  };

  const handleDelete = async (story) => {
    if (!window.confirm("Delete this story? This can't be undone.")) return;
    try {
      await axios.delete(`${API}/stories/${story.id}`, { withCredentials: true });
      track("story_delete", {
        story_id: story.id,
        is_own: me?.user_id === story.user_id,
      });
      toast.success("Story deleted");
      setStories((prev) => prev.filter((s) => s.id !== story.id));
    } catch (err) {
      console.error("Delete story failed:", err);
      toast.error("Could not delete story");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F5]">
      <Toaster position="top-center" richColors />
      <header className="border-b border-[#E4E1D9] px-6 md:px-12 py-4 flex items-center gap-4">
        <button
          type="button"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-[#6B6A68] hover:text-[#1A1A1A] transition-colors text-xs uppercase tracking-[0.2em]"
          style={{ fontFamily: "IBM Plex Mono, monospace" }}
          data-testid="stories-back"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <h1
          className="text-2xl md:text-3xl tracking-tighter ml-2"
          style={{ fontFamily: "Cormorant Garamond, serif" }}
        >
          North of Six Club
        </h1>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-10 md:py-16">
        <p
          className="text-[10px] md:text-xs uppercase tracking-[0.3em] text-[#6B6A68] mb-2"
          style={{ fontFamily: "IBM Plex Mono, monospace" }}
        >
          Your Story
        </p>
        <h2
          className="text-4xl md:text-5xl tracking-tighter text-[#2D302A] mb-10 md:mb-14"
          style={{ fontFamily: "Cormorant Garamond, serif" }}
        >
          Stories of being tall
        </h2>

        {loading ? (
          <div className="space-y-10">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-56 bg-[#E4E1D9] rounded-md mb-4" />
                <div className="h-4 bg-[#E4E1D9] rounded w-1/3 mb-2" />
                <div className="h-3 bg-[#E4E1D9] rounded w-full" />
              </div>
            ))}
          </div>
        ) : stories.length === 0 ? (
          <p className="text-[#6B6A68]">No stories yet. Be the first to share yours from the homepage.</p>
        ) : (
          <div className="space-y-12 md:space-y-16">
            {stories.map((story) => (
              <article
                key={story.id}
                id={`story-${story.id}`}
                className="scroll-mt-6 bg-white border border-[#E4E1D9] rounded-md overflow-hidden"
                data-testid="story-item"
              >
                {story.image_url && (
                  // Fixed aspect ratio reserves the image's height before it
                  // loads. Without it, lazy images sit at 0px until decoded and
                  // then snap to full height as you scroll past — the reflow is
                  // what made scrolling feel laggy and jittery.
                  <img
                    src={`${BACKEND_URL}${story.image_url}`}
                    alt=""
                    loading="lazy"
                    className="w-full aspect-[4/5] object-cover bg-[#F0EFEB]"
                  />
                )}
                <div className="p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Avatar name={story.user_name} picture={story.user_picture} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm text-[#2D302A] truncate">{story.user_name}</p>
                        <StoryTags story={story} />
                      </div>
                    </div>
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={() => setEditingStory(story)}
                        className="ml-auto p-2 text-[#9A9A98] hover:text-[#2D302A] transition-colors"
                        title="Edit story"
                        aria-label="Edit story"
                        data-testid="story-edit-button"
                      >
                        <PencilSimple size={18} />
                      </button>
                    )}
                    {canDelete(story) && (
                      <button
                        type="button"
                        onClick={() => handleDelete(story)}
                        className={`${isAdmin ? "" : "ml-auto "}p-2 text-[#9A9A98] hover:text-red-600 transition-colors`}
                        title="Delete story"
                        aria-label="Delete story"
                        data-testid="story-delete-button"
                      >
                        <Trash size={18} />
                      </button>
                    )}
                  </div>
                  {/* content_html is sanitized server-side (allowlist, no links) */}
                  <div
                    className="story-body text-[15px] md:text-base leading-relaxed text-[#2D302A]"
                    dangerouslySetInnerHTML={{ __html: story.content_html }}
                  />
                  <div className="mt-5 pt-4 border-t border-[#F0EFEB]">
                    <button
                      type="button"
                      onClick={() => handleLike(story)}
                      className={`inline-flex items-center gap-2 text-sm transition-colors ${
                        story.liked ? "text-[#C0392B]" : "text-[#9A9A98] hover:text-[#C0392B]"
                      }`}
                      aria-label={story.liked ? "Remove heart" : "Heart this story"}
                      aria-pressed={story.liked}
                      data-testid="story-like-button"
                    >
                      <Heart size={20} weight={story.liked ? "fill" : "regular"} />
                      {story.like_count > 0 && (
                        <span style={{ fontFamily: "IBM Plex Mono, monospace" }} className="text-xs">
                          {story.like_count}
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </article>
            ))}
            {hasMore && (
              <div ref={sentinelRef} className="py-6 flex justify-center">
                <PaginationLoader active={loadingMore} />
              </div>
            )}
          </div>
        )}
      </main>

      <StoryComposer
        open={!!editingStory}
        story={editingStory}
        onClose={() => setEditingStory(null)}
        onSuccess={({ id, content_html }) =>
          setStories((prev) => prev.map((s) => (s.id === id ? { ...s, content_html } : s)))
        }
      />
    </div>
  );
};

export default Stories;
